// src/components/layout/Footer.jsx
import { useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import logo from '../../Assets/Logo/SridharFooterlogo.png';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { pathname, key } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTo(0, 0);
    document.body.scrollTo(0, 0);
    const mainContent = document.querySelector('main') || document.querySelector('#root');
    if (mainContent) mainContent.scrollTo(0, 0);
  }, [pathname, key]);

  // Consistent active/inactive classes for all links
  const activeClass = ({ isActive }) =>
    isActive
      ? "text-blue-400 underline underline-offset-2 decoration-2 transition-all duration-300"
      : "text-slate-400 hover:text-blue-400 transition-all duration-300";

  // Single source of truth for every column heading - 18px
  const headingClass =
    "text-[18px] font-semibold text-white mb-3 md:mb-5 pb-2 border-b border-blue-700/50 inline-block";

  // Single source of truth for every list item / body text - 14px
  const bodyTextClass = "text-[14px]";

  // Single source of truth for vertical rhythm INSIDE each column
  const listSpacingClass = "space-y-3 md:space-y-4";

  return (
    <footer
      className="bg-slate-950 text-slate-300"
      style={{
        // Height of your fixed mobile bottom nav (Book Appointment / Call Now / WhatsApp)
        ['--bottom-nav-h']: '80px',
        // Extra breathing room ABOVE the bottom nav
        ['--bottom-safe-gap']: '1.5rem',
      }}
    >
      {/* Main footer content */}
      <div className="container mx-auto px-4 sm:px-6 py-8 md:py-12 lg:py-16">
        <div className="grid grid-cols-1 gap-y-10 gap-x-8 md:grid-cols-2 lg:grid-cols-5 items-start">
          
          {/* Column 1 - Logo & About */}
          <div className="flex flex-col h-full">
            <Link to="/" className="mb-4 md:mb-6 block transition-all duration-300 hover:opacity-80">
              <img src={logo} alt="Dr. Sridhar International Dental Hospitals Logo" className="h-12 sm:h-14 w-auto object-contain" />
            </Link>
            <p className={`text-slate-400 leading-relaxed mb-4 md:mb-6 max-w-md ${bodyTextClass}`}>
              Sridhar International Dental Hospitals – Restoring confident smiles since 1995.
              Advanced technology, compassionate experts, and personalized care for every generation.
            </p>
            <div className="flex items-center gap-4 md:gap-5">
              <a href="https://www.facebook.com/sridhardental" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-slate-400 hover:text-blue-400 transition-colors duration-300 text-lg md:text-xl"><i className="fab fa-facebook-f"></i></a>
              <a href="https://www.instagram.com/dr.sridharreddy_arumalla" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-slate-400 hover:text-pink-400 transition-colors duration-300 text-lg md:text-xl"><i className="fab fa-instagram"></i></a>
              <a href="https://www.linkedin.com/company/dr-sridhar-international-dental-hospital-&-research-centre" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-slate-400 hover:text-blue-500 transition-colors duration-300 text-lg md:text-xl"><i className="fab fa-linkedin-in"></i></a>
              <a
                href="https://www.youtube.com/@DrARUMALLASRIDHARREDDY"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="text-slate-400 hover:text-red-500 transition-colors duration-300 text-lg md:text-xl"
              >
                <i className="fab fa-youtube"></i>
              </a>
            </div>
          </div>

          {/* Column 2 - Quick Links */}
          <div className="flex flex-col h-full">
            <h4 className={headingClass}>Quick Links</h4>
            <ul className={`${listSpacingClass} flex flex-col ${bodyTextClass}`}>
              <li><NavLink to="/about" className={activeClass}>About Us</NavLink></li>
              <li><NavLink to="/services" className={activeClass}>Our Services</NavLink></li>
              <li><NavLink to="/gallery" className={activeClass}>Gallery</NavLink></li>
              <li><NavLink to="/blog" className={activeClass}>Our Blog</NavLink></li>
              <li><NavLink to="/contact" className={activeClass}>Contact Us</NavLink></li>
            </ul>
          </div>

          {/* Column 3 - Our Services */}
          <div className="flex flex-col h-full">
            <h4 className={headingClass}>Our Services</h4>
            <ul className={`${listSpacingClass} flex flex-col ${bodyTextClass}`}>
              <li><NavLink to="/service/smile-Designing" className={activeClass}>Smile Designing &amp; Cosmetic Dentistry</NavLink></li>
              <li><NavLink to="/service/dental-implants" className={activeClass}>Dental Implants</NavLink></li>
              <li><NavLink to="/service/periodontal-treatment" className={activeClass}>Periodontal Treatment</NavLink></li>
              <li><NavLink to="/service/laser-dentistry" className={activeClass}>Laser Dentistry</NavLink></li>
              <li><NavLink to="/service/root-canal" className={activeClass}>Root Canal (Endodontics)</NavLink></li>
              <li><NavLink to="/service/oral-surgery" className={activeClass}>Oral &amp; Maxillofacial Surgery</NavLink></li>
              <li><NavLink to="/service/pediatric-dentistry" className={activeClass}>Pediatric Dentistry</NavLink></li>
              <li><NavLink to="/service/crownsBridges-dentures" className={activeClass}>Crowns, Bridges &amp; Dentures</NavLink></li>
              <li><NavLink to="/service/invisalign" className={activeClass}>Orthodontics</NavLink></li>
              <li><NavLink to="/service/advanced-dentures" className={activeClass}>Advanced Denture Solutions</NavLink></li>
              <li><NavLink to="/service/radiology-department" className={activeClass}>Dental Radiology &amp; Diagnostics</NavLink></li>
            </ul>
          </div>

          {/* Column 4 - Support */}
          <div className="flex flex-col h-full">
            <h4 className={headingClass}>Support</h4>
            <ul className={`${listSpacingClass} flex flex-col ${bodyTextClass}`}>
              <li><NavLink to="/case-study" className={activeClass}>Case Studies</NavLink></li>
              <li><NavLink to="/doctor-team" className={activeClass}>Our Doctors</NavLink></li>
              <li><NavLink to="/faq" className={activeClass}>FAQ</NavLink></li>
              <li><NavLink to="/take-a-tour" className={activeClass}>Take A Tour</NavLink></li>
            </ul>
          </div>

          {/* Column 5 - Contact */}
          <div className="flex flex-col h-full">
            <h4 className={headingClass}>Get in Touch</h4>
            <div className={`${listSpacingClass} ${bodyTextClass}`}>
              <div className="flex items-start gap-3">
                <span className="text-blue-400 flex-shrink-0" aria-hidden="true">📍</span>
                <p className="leading-relaxed">
                  Vijayawada • Hyderabad<br />
                  <span className="text-slate-500">(Main branches in AP &amp; TS)</span>
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-blue-400 text-base md:text-lg flex-shrink-0" aria-hidden="true">📞</span>
                <div className="flex flex-col">
                  <a href="tel:+919490948081" className="hover:text-blue-400">+91 94909 48081</a>
                  <a href="tel:+919542027700" className="hover:text-blue-400">+91 95420 27700</a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-blue-400 flex-shrink-0" aria-hidden="true">✉️</span>
                <a href="mailto:dr.sridhardental@gmail.com" className="hover:text-blue-400 break-words leading-relaxed">dr.sridhardental@gmail.com</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer - Extra space only on mobile/tablet (fixed nav present), reduced on lg+ */}
      <div
        className="bg-black/60 px-4 py-4 sm:py-5 md:py-6 border-t border-slate-800 pt-[var(--bottom-safe-gap)] pb-[calc(var(--bottom-safe-gap)+var(--bottom-nav-h)+env(safe-area-inset-bottom))] lg:pb-[var(--bottom-safe-gap)]"
      >
        <p className={`text-slate-400 text-center leading-relaxed break-words ${bodyTextClass}`}>
          © {currentYear} DR. SRIDHAR International Dental Hospitals – All rights reserved
        </p>

        <p className={`mt-1.5 sm:mt-2 text-slate-400 text-center leading-relaxed break-words ${bodyTextClass}`}>
          Specialized Dental Care |
          <br className="sm:hidden" />
          <span className="sm:ml-1">
            Designed &amp; Developed by{" "}
            <a
              href="https://www.oakyaitservices.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-blue-400 transition-colors"
            >
              Oakya IT Services Pvt Ltd
            </a>
          </span>
        </p>
      </div>
    </footer>
  );
};

export default Footer;